/**
 * Which language the app speaks: the URL first (public pages always carry it: /de/…, /en/…), then
 * the user's own choice (the language switch, kept in this browser), then the browser's
 * languages; German otherwise.
 *
 * Adding a language (es, id, pl, it are planned): add it to LANGUAGES, give every namespace a
 * `<lang>.json` next to `de.json` (the key parity test lists what is missing) and register it in
 * `app/i18n.ts`. Texts not yet translated fall back to German.
 */

export const LANGUAGES = ['de', 'en'] as const;
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = 'de';

/** Next in line; their structure is prepared, their texts are not written yet. */
export const PLANNED_LANGUAGES = ['es', 'id', 'pl', 'it'] as const;

const STORAGE_KEY = 'planner.language';

export const isLanguage = (value: unknown): value is Language => LANGUAGES.includes(value as Language);

/** The user's own choice in this browser, if any (storage may be unavailable). */
export function storedLanguage(): Language | undefined {
  try {
    const value = globalThis.localStorage.getItem(STORAGE_KEY);
    return isLanguage(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Switches the app to `language` and remembers the choice. */
export async function chooseLanguage(
  translations: { changeLanguage: (language: string) => Promise<unknown> },
  language: Language,
): Promise<void> {
  rememberLanguage(language);
  await translations.changeLanguage(language);
}

/** The same choice for the server: the start address / redirects by it (Caddyfile, SEO plan T-2). */
const COOKIE = 'planner_language';
const YEAR_SECONDS = 365 * 24 * 60 * 60;

export function rememberLanguage(language: Language): void {
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Not remembered: the URL and the browser still decide.
  }
  if (typeof document === 'undefined') return;
  const secure = globalThis.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE}=${language}; Path=/; Max-Age=${String(YEAR_SECONDS)}; SameSite=Lax${secure}`;
}

/** The first browser language the app speaks. */
export function browserLanguage(languages: readonly string[] = navigator.languages): Language {
  return languages.map((tag) => tag.slice(0, 2)).find(isLanguage) ?? DEFAULT_LANGUAGE;
}

export function preferredLanguage(urlLanguage?: string): Language {
  if (isLanguage(urlLanguage)) return urlLanguage;
  return storedLanguage() ?? browserLanguage();
}

/** A public page in `language`: `/about` → `/en/about`, `/` → `/en`. */
export function localizedPath(path: string, language: Language): string {
  return path === '/' ? `/${language}` : `/${language}${path}`;
}
